import { ChevronDown, ExternalLink, Plug } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Switch } from "~/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import type { AgentConnector } from "~/services/agents";
import type { McpGuidelines } from "./useMcpGuidelines";

const MCP_GUIDELINES_URL = "https://www.manifold.ai/manifold-mcp-guidelines";
const NO_CONNECTORS_MESSAGE = "No connectors are available for this agent.";

interface ConnectorsPopoverProps {
  connectors: AgentConnector[];
  /** Connector names currently enabled for this conversation. */
  enabledNames: string[];
  onToggle: (connectorName: string) => void;
  /** MCP-guidelines acceptance gate. Connectors cannot be enabled until the
   *  user accepts, since they connect to third-party products. */
  mcpGuidelines: McpGuidelines;
}

export const ConnectorsPopover = ({
  connectors,
  enabledNames,
  onToggle,
  mcpGuidelines,
}: ConnectorsPopoverProps) => {
  const { accepted, isAccepting, onAccept } = mcpGuidelines;
  // Toggles stay locked until the user has accepted the guidelines. `null` is
  // the still-loading state, so anything other than an explicit `true` locks.
  const locked = accepted !== true;

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={connectors.length === 0}
      className="h-8 gap-1.5 px-2 text-xs text-muted-foreground"
    >
      <Plug className="size-3.5" />
      Connectors ({enabledNames.length})
      <ChevronDown className="size-3.5" />
    </Button>
  );

  // No connectors: disable the trigger and explain via tooltip rather than
  // opening an empty popover. The span keeps the tooltip hoverable since the
  // disabled button has pointer-events: none.
  if (connectors.length === 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{trigger}</span>
        </TooltipTrigger>
        <TooltipContent>{NO_CONNECTORS_MESSAGE}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0">
        <div className="border-b px-3 py-2">
          <p className="text-sm font-semibold">Connectors</p>
          <p className="text-xs text-muted-foreground">
            Choose which connectors this conversation can use.
          </p>
        </div>

        {accepted === false && (
          <div className="border-b px-3 py-2">
            <div className="flex items-center gap-3 rounded-md border bg-muted px-3 py-2">
              <p className="flex-1 text-xs text-muted-foreground">
                By using MCPs, you&#39;ll be connecting to third-party products. See our{" "}
                <a
                  href={MCP_GUIDELINES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  MCP guidelines
                </a>
                .
              </p>
              <Button size="sm" loading={isAccepting} onClick={onAccept} className="shrink-0">
                Accept
              </Button>
            </div>
          </div>
        )}

        <ul className="flex max-h-80 flex-col divide-y overflow-y-auto px-3 py-2" role="list">
          {connectors.map(({ connector }) => {
            const enabled = enabledNames.includes(connector.name);
            return (
              <li key={connector.id} className="flex items-center justify-between gap-4 py-3">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">
                    {connector.display_name ?? connector.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {connector.description}
                    {connector.url && (
                      <>
                        {" "}
                        <a
                          href={connector.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 underline hover:text-foreground"
                        >
                          Learn more
                          <ExternalLink className="size-3" />
                        </a>
                      </>
                    )}
                  </span>
                </div>
                <Switch
                  checked={enabled}
                  disabled={locked}
                  onCheckedChange={() => onToggle(connector.name)}
                  aria-label={`Toggle ${connector.display_name ?? connector.name}`}
                />
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
